import { useEffect, useState } from 'react';
import { BookOpen, ArrowUpRight, ChevronDown } from 'lucide-react';
import { pedirCatalogo, type FuenteDelCatalogo } from '../../../common/services/catalogo.js';
import { BASE } from '../../../common/services/api.js';
import { MARCA_DE_FRAGMENTO } from '@athena/shared';
import { enlaceAlDocumento, formatearCita, idDeLaReferencia, referenciasDe, resolverGrafico } from '@athena/shared';
import { anotarEvento } from '../../../common/services/api.js';
import { diccionarioDe, type Idioma } from '../../../i18n/index.js';
import type { RespuestaDeAthena } from '../../../common/services/tipos.js';
import { ENLACE_A_EDITOR } from '../../../common/utils/enlaces.js';

/**
 * La lista de fuentes, con la cita literal de cada una y el enlace a su página.
 *
 * Es la comprobación 5 del goal hecha pantalla: el enlace abre el documento **por la página del
 * dato**. Cuando la fuente no tiene página (una web sin paginación), el enlace va al documento a
 * secas: inventarse un ancla sería peor que no ponerlo.
 *
 * La cita va con su `lang` propio, porque la mayoría están en alemán, francés o inglés y un lector
 * de pantalla en español las leería como si fueran español.
 */
export function ListaFuentes({ respuesta, idioma }: { readonly respuesta: RespuestaDeAthena; readonly idioma: Idioma }) {
  const t = diccionarioDe(idioma).consulta;
  const [catalogo, setCatalogo] = useState<readonly FuenteDelCatalogo[]>([]);
  useEffect(() => {
    let vigente = true;
    void pedirCatalogo().then(c => {
      if (vigente && c !== null) setCatalogo(c.fuentes);
    });
    return () => {
      vigente = false;
    };
  }, []);
  if (respuesta.fuentes.length === 0) return null;

  const numeroDe = (hechoId: string) => respuesta.referencias.find(r => r.hechoId === hechoId)?.n ?? null;

  // Los datos que solo cita un gráfico no son ocho filas iguales: son una, la del gráfico. Un dato
  // que además lleva marca [n] o lo cita otro bloque sigue saliendo solo, con su cita.
  const citadosPorOtros = new Set([
    ...respuesta.referencias.map(r => r.hechoId),
    ...respuesta.bloques.filter(b => b.tipo !== 'Grafico').flatMap(b => referenciasDe(b).map(idDeLaReferencia)),
  ]);
  // Solo los gráficos que la web llega a pintar: si no se pinta, sus datos no tienen por qué salir en la lista.
  const hechos = new Map(respuesta.fuentes.map(f => [f.hechoId, f]));
  const graficos = respuesta.bloques.flatMap(b => {
    if (b.tipo !== 'Grafico' || resolverGrafico(b, hechos) === null) return [];
    const propios = [...new Set(referenciasDe(b))].map(r => hechos.get(idDeLaReferencia(r))!).filter(f => !citadosPorOtros.has(f.hechoId));
    const grupos = new Map<string, typeof propios>();
    for (const f of propios) {
      const clave = `${f.fuenteSlug}#${String(f.pagina)}`;
      grupos.set(clave, [...(grupos.get(clave) ?? []), f]);
    }
    return [...grupos.values()].map(datos => ({
      titulo: b.titulo,
      datos,
      puntos: new Set(
        referenciasDe(b)
          .map(idDeLaReferencia)
          .filter(id => {
            const f = hechos.get(id)!;
            return f.fuenteSlug === datos[0]!.fuenteSlug && f.pagina === datos[0]!.pagina;
          }),
      ).size,
    }));
  });
  const soloDeGraficos = new Set(graficos.flatMap(g => g.datos.map(f => f.hechoId)));
  const ordenadas = respuesta.fuentes
    .filter(f => !soloDeGraficos.has(f.hechoId))
    .sort((a, b) => (numeroDe(a.hechoId) ?? 99) - (numeroDe(b.hechoId) ?? 99));

  const estudios = [...new Set(respuesta.fuentes.map(f => f.fuenteSlug))];
  return (
    <section className='min-w-0 border-t border-(--line) pt-6 min-[1100px]:border-t-0 min-[1100px]:pt-0'>
      <h2 className='m-0 mb-4 font-(family-name:--text) text-[13px] font-semibold tracking-[0.06em] text-(--ink-faint) uppercase'>{t.fuentes}</h2>
      <div className='flex flex-col gap-4'>
        {estudios.map(slug => {
          const fuente = respuesta.fuentes.find(f => f.fuenteSlug === slug)!;
          const ficha = catalogo.find(f => f.slug === slug);
          const datos = ordenadas.filter(f => f.fuenteSlug === slug);
          const grupos = graficos.filter(g => g.datos[0]!.fuenteSlug === slug);
          const referencias = datos.length + grupos.length;
          const numeros = datos.map(f => numeroDe(f.hechoId)).filter(n => n !== null);
          const original = fuente.urlDocumento ?? fuente.url;
          const portada = ficha?.portadaUrl;
          const ruta = portada?.startsWith('/corpus/') === true ? `${BASE}${portada}` : portada;
          return (
            <article key={slug} className='overflow-hidden rounded-2xl border border-(--line-strong) bg-(--paper) shadow-sm'>
              <div className='flex items-start gap-4 p-5'>
                <a
                  href={`/fuentes/${encodeURIComponent(slug)}`}
                  aria-label={ficha?.tituloOriginal ?? fuente.editor}
                  className='flex w-[72px] shrink-0 items-center justify-center overflow-hidden rounded border border-(--line) bg-(--chat-bg)'
                >
                  <PortadaDeFuente ruta={ruta} />
                </a>
                <div className='min-w-0'>
                  <h3 className='m-0 font-(family-name:--text) text-[15px] leading-[1.4] font-semibold text-(--ink) break-words'>
                    <a href={`/fuentes/${encodeURIComponent(slug)}`} className='text-inherit no-underline hover:underline'>
                      {ficha?.tituloOriginal ?? fuente.editor}
                    </a>
                  </h3>
                  <p className='m-0 mt-2 font-(family-name:--text) text-[12px] leading-[1.5] text-(--ink-soft)'>
                    {fuente.editor}
                    {fuente.anio === null ? '' : ` · ${String(fuente.anio)}`}
                  </p>
                  {original ? (
                    <a
                      href={original}
                      {...ENLACE_A_EDITOR}
                      onClick={() => anotarEvento('clic_fuente', respuesta.preguntaId)}
                      className='mt-3 inline-flex items-center gap-1 font-(family-name:--text) text-[12px] font-medium text-(--teal-deep)'
                    >
                      {t.leerOriginal}
                      <ArrowUpRight size={14} aria-hidden='true' />
                    </a>
                  ) : null}
                </div>
              </div>
              <details className='group border-t border-(--line)'>
                <summary className='flex cursor-pointer list-none items-center justify-between gap-3 bg-(--chat-bg) px-5 py-3 font-(family-name:--text) text-[12px] text-(--ink-soft) [&::-webkit-details-marker]:hidden'>
                  <span className='min-w-0 break-words'>
                    {t.verReferencias.replace('{n}', String(referencias))}
                    <span className='ml-2 font-(family-name:--mono) text-(--teal-deep)'>
                      {numeros
                        .slice(0, 4)
                        .map(n => `[${String(n)}]`)
                        .join(' ')}
                      {numeros.length > 4 ? ' …' : null}
                    </span>
                  </span>
                  <ChevronDown size={15} aria-hidden='true' className='shrink-0 transition-transform group-open:rotate-180' />
                </summary>
                <ol className='m-0 flex list-none flex-col gap-5 p-5'>
                  {datos.map(f => {
                    const n = numeroDe(f.hechoId);
                    // Un dato de tabla se enseña por su tabla y su página, como el de gráfico: su «cita» es la
                    // fila de celdas («Hotels 451 356 356 44%»), literal pero ilegible entre comillas (BUS-21).
                    const porPagina = f.cita === null || (f.origen === 'tabla' && f.localizador !== null);
                    return (
                      <li
                        key={f.hechoId}
                        id={n === null ? undefined : `f${String(n)}-${respuesta.preguntaId}`}
                        tabIndex={-1}
                        className='scroll-mt-24 rounded font-(family-name:--text) focus:outline-2 focus:outline-offset-4 focus:outline-(--teal-deep)'
                      >
                        <p className='m-0 text-[14px] text-(--ink)'>
                          {n === null ? null : <span className='mr-2 font-(family-name:--mono) text-[12px] text-(--teal-deep)'>[{String(n)}]</span>}
                          {formatearCita(f, idioma)}
                        </p>
                        {porPagina ? (
                          // Sin frase literal (gráfico, celda de tabla o página escaneada): se dice de dónde sale
                          // el dato, sin comillas, porque no hay ninguna frase que entrecomillar.
                          <p className='my-[6px] border-l-2 border-(--line-strong) pl-3 text-[14px] leading-[1.55] text-(--ink-soft)'>
                            {f.localizador ?? t.sinFrase}
                          </p>
                        ) : (
                          <blockquote className='my-[6px] border-l-2 border-(--line-strong) pl-3 text-[14px] leading-[1.55] text-(--ink-soft) italic'>
                            <q lang={idiomaDeLaCita(f.fuenteSlug)}>{f.textoEn.startsWith(MARCA_DE_FRAGMENTO) ? citaVisible(f.cita) : f.cita}</q>
                          </blockquote>
                        )}
                        <p className='m-0 flex flex-wrap items-center gap-3 text-[12px]'>
                          {f.urlDocumento !== null ? (
                            <a
                              href={enlaceAlDocumento(f.urlDocumento, f.pagina)}
                              {...ENLACE_A_EDITOR}
                              className='text-(--teal-deep)'
                              onClick={() => anotarEvento('clic_fuente', respuesta.preguntaId)}
                            >
                              {t.abrirDocumento}
                            </a>
                          ) : f.url === undefined || f.url === null ? null : (
                            // Sin PDF, la página web del estudio, sin ancla: `#page=` no significa nada en una web.
                            <a
                              href={f.url}
                              {...ENLACE_A_EDITOR}
                              className='text-(--teal-deep)'
                              onClick={() => anotarEvento('clic_fuente', respuesta.preguntaId)}
                            >
                              {t.abrirPagina}
                            </a>
                          )}
                          {/* Toda cita que llega aquí pasó la puerta literal contra su página: la búsqueda solo lee
                    hechos vigentes. `verificado` es otra cosa (venía de una ficha de septiembre) y ya no
                    distingue nada, así que no se enseña como si dijera algo de la página. */}
                          <span className='text-(--ink-faint)'>{porPagina ? t.compruebaEnLaPagina : t.verificada}</span>
                        </p>
                      </li>
                    );
                  })}
                  {grupos.map(g => {
                    const f = g.datos[0]!;
                    return (
                      <li key={`grafico-${g.titulo}-${f.hechoId}`} className='font-(family-name:--text)'>
                        <p className='m-0 text-[14px] text-(--ink)'>
                          {t.grafico.etiqueta}: {g.titulo} ({t.grafico.datos.replace('{n}', String(g.puntos))}). {formatearCita(f, idioma)}
                        </p>
                        <p className='my-[6px] border-l-2 border-(--line-strong) pl-3 text-[14px] leading-[1.55] text-(--ink-soft)'>
                          {f.localizador ?? t.grafico.compruebaEnLaPagina}
                        </p>
                        <p className='m-0 flex flex-wrap items-center gap-3 text-[12px]'>
                          {f.urlDocumento === null ? null : (
                            <a
                              href={enlaceAlDocumento(f.urlDocumento, f.pagina)}
                              {...ENLACE_A_EDITOR}
                              className='text-(--teal-deep)'
                              onClick={() => anotarEvento('clic_fuente', respuesta.preguntaId)}
                            >
                              {t.abrirDocumento}
                            </a>
                          )}
                          <span className='text-(--ink-faint)'>{t.grafico.compruebaEnLaPagina}</span>
                        </p>
                      </li>
                    );
                  })}
                </ol>
              </details>
            </article>
          );
        })}
      </div>
    </section>
  );
}

/**
 * De qué idioma es la cita, adivinado por el slug de la fuente.
 *
 * Es una aproximación y se dice: el idioma real vive en `fuentes.idioma`, que hoy no viaja en la
 * respuesta. Mientras no viaje, esto es mejor que marcarlo todo como español, que es lo que haría
 * el navegador por defecto y lo que haría que un lector de pantalla leyera alemán en español.
 */
export function idiomaDeLaCita(fuenteSlug: string): string | undefined {
  if (/auma|messe|veranstalter|deutsch/u.test(fuenteSlug)) return 'de';
  if (/tude-|tendances|omnium|v-nementiel|guide-complet/u.test(fuenteSlug)) return 'fr';
  if (/informe|mercado|eventoplus|espa/u.test(fuenteSlug)) return 'es';
  return 'en';
}

/**
 * BUS-89: un fragmento de página cita ~1.200 caracteres literales; solo a los fragmentos se les recorta
 * (la cita de un hecho, aunque sea larga, sigue literal y entera); entre comillas en la lista de
 * fuentes ahogaría la respuesta. Se enseña el principio, hasta un final de palabra, con «…»; el
 * enlace al documento y la página siguen debajo. Las citas de un hecho son una frase y no se tocan.
 */
export const LARGO_CITA_VISIBLE = 320;
export function citaVisible(cita: string | null): string | null {
  if (cita === null || cita.length <= LARGO_CITA_VISIBLE) return cita;
  const corte = cita.lastIndexOf(' ', LARGO_CITA_VISIBLE);
  return `${cita.slice(0, corte > 200 ? corte : LARGO_CITA_VISIBLE).trimEnd()}…`;
}

/** Una portada ausente o fallida conserva el mismo espacio y un icono discreto. */
function PortadaDeFuente({ ruta }: { readonly ruta: string | null | undefined }) {
  const [fallida, setFallida] = useState<string | null>(null);
  return !ruta || ruta === fallida ? (
    <BookOpen aria-hidden='true' size={28} className='my-6 text-(--ink-faint)' />
  ) : (
    <img src={ruta} alt='' loading='lazy' className='block h-auto max-h-[110px] w-full object-contain' onError={() => setFallida(ruta)} />
  );
}
